/**
 * Компонент для отображения свойств слоя, сгруппированных по категориям
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import { LAYER_ATTRIBUTE_CATEGORIES } from '@/data/layerAttributeCategories';
import { isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import { formatAttributeLabel } from '@/utils/attributeLabel';

const PROPERTY_DECIMAL_PLACES = 3;
const PROPERTY_DECIMAL_FACTOR = 10 ** PROPERTY_DECIMAL_PLACES;

interface LayerProperty {
  id?: string;
  name: string;
  unit?: string | null;
  value?: string | number | null;
  value_type?: string;
  description?: string | null;
}

interface LayerPropertiesByCategoryProps {
  properties: LayerProperty[];
  propertiesData: { [key: string]: string | number };
  onPropertyChange?: (propertyName: string, value: string | number) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

export const LayerPropertiesByCategory: React.FC<LayerPropertiesByCategoryProps> = ({
  properties,
  propertiesData,
  onPropertyChange,
  disabled = false,
  readOnly = false,
}) => {
  const [inputStrings, setInputStrings] = React.useState<Record<string, string>>({});
  const visibleProperties = React.useMemo(
    () => properties.filter((prop) => !isHiddenLayerAttribute(prop.id, prop.name)),
    [properties],
  );

  React.useEffect(() => {
    setInputStrings({});
  }, [propertiesData]);

  // Создаем мапу свойств по имени для быстрого доступа
  const propertiesMap = new Map<string, LayerProperty>();
  visibleProperties.forEach(prop => {
    propertiesMap.set(prop.name, prop);
  });

  // Группируем свойства по категориям
  const categorizedProperties = LAYER_ATTRIBUTE_CATEGORIES.map(category => {
    const categoryProps = category.attributes
      .map(attrName => {
        const prop = propertiesMap.get(attrName);
        if (!prop) return null;

        return {
          ...prop,
          category: category,
        };
      })
      .filter((prop): prop is LayerProperty & { category: typeof category } => prop !== null);

    return {
      category,
      properties: categoryProps,
    };
  }).filter(group => group.properties.length > 0);

  // Отладочная информация (можно убрать после проверки)
  if (process.env.NODE_ENV === 'development' && categorizedProperties.length === 0 && visibleProperties.length > 0) {
    console.log('LayerPropertiesByCategory: No categories matched. Properties:', visibleProperties.map(p => p.name));
    console.log('Available category attributes:', LAYER_ATTRIBUTE_CATEGORIES.flatMap(c => c.attributes));
  }

  const handlePropertyChange = (propertyName: string, value: string | number) => {
    if (onPropertyChange && !readOnly && !disabled) {
      onPropertyChange(propertyName, value);
    }
  };

  const getPropertyValue = (property: LayerProperty): string => {
    if (Object.prototype.hasOwnProperty.call(inputStrings, property.name)) {
      return inputStrings[property.name];
    }

    const value = propertiesData[property.name];
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (property.value_type === 'float') {
      const numericValue = typeof value === 'number' ? value : Number(value);
      if (!Number.isNaN(numericValue)) {
        return readOnly ? numericValue.toFixed(PROPERTY_DECIMAL_PLACES) : String(numericValue);
      }
    }
    return String(value);
  };

  const handleNumericFieldChange = (property: LayerProperty, rawValue: string) => {
    if (property.value_type === 'float') {
      const floatRegex = /^-?\d*(\.\d{0,3})?$/;
      if (!floatRegex.test(rawValue)) return;
      setInputStrings((prev) => ({ ...prev, [property.name]: rawValue }));
      if (rawValue === '' || rawValue === '-' || rawValue === '.' || rawValue === '-.') {
        handlePropertyChange(property.name, '');
        return;
      }
      const parsed = parseFloat(rawValue);
      if (!Number.isNaN(parsed)) {
        handlePropertyChange(property.name, Math.round(parsed * PROPERTY_DECIMAL_FACTOR) / PROPERTY_DECIMAL_FACTOR);
      }
      return;
    }

    if (property.value_type === 'integer') {
      const intRegex = /^-?\d*$/;
      if (!intRegex.test(rawValue)) return;
      setInputStrings((prev) => ({ ...prev, [property.name]: rawValue }));
      if (rawValue === '' || rawValue === '-') {
        handlePropertyChange(property.name, '');
        return;
      }
      const parsed = parseInt(rawValue, 10);
      if (!Number.isNaN(parsed)) {
        handlePropertyChange(property.name, parsed);
      }
      return;
    }

    handlePropertyChange(property.name, rawValue);
  };

  const handleNumericFieldBlur = (property: LayerProperty) => {
    const rawValue = inputStrings[property.name];
    if (rawValue === undefined) return;

    if (property.value_type === 'float') {
      if (rawValue === '' || rawValue === '-' || rawValue === '.' || rawValue === '-.') {
        handlePropertyChange(property.name, '');
      } else {
        const parsed = parseFloat(rawValue);
        if (!Number.isNaN(parsed)) {
          handlePropertyChange(property.name, Math.round(parsed * PROPERTY_DECIMAL_FACTOR) / PROPERTY_DECIMAL_FACTOR);
        }
      }
    }

    if (property.value_type === 'integer') {
      if (rawValue === '' || rawValue === '-') {
        handlePropertyChange(property.name, '');
      } else {
        const parsed = parseInt(rawValue, 10);
        if (!Number.isNaN(parsed)) {
          handlePropertyChange(property.name, parsed);
        }
      }
    }

    setInputStrings((prev) => {
      const next = { ...prev };
      delete next[property.name];
      return next;
    });
  };

  const getInputType = (property: LayerProperty): string => {
    const type = property.value_type || 'string';
    if (type === 'integer' || type === 'float') {
      return 'number';
    }
    return 'text';
  };

  // Если нет категоризированных свойств, показываем сообщение
  if (categorizedProperties.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Нет свойств для отображения
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {categorizedProperties.map(({ category, properties: categoryProps }) => (
        <Accordion key={category.id} defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${category.id}-content`}
            id={`${category.id}-header`}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              {category.name}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {categoryProps.map((property) => (
                <Grid item xs={12} sm={6} key={property.name}>
                  <TextField
                    fullWidth
                    label={formatAttributeLabel(property.name, property.unit)}
                    value={getPropertyValue(property)}
                    onChange={(e) => handleNumericFieldChange(property, e.target.value)}
                    onBlur={() => handleNumericFieldBlur(property)}
                    helperText={property.description || undefined}
                    size="small"
                    disabled={disabled || readOnly}
                    type={getInputType(property)}
                    inputProps={
                      property.value_type === 'float'
                        ? { step: '0.001' }
                        : property.value_type === 'integer'
                        ? { step: 1 }
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
